/** @jsx jsx */
import { useEffect, type ReactNode } from 'react';
import { jsx, css } from '@compiled/react';
import { xcss, Grid } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { HorizontalNavSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import {
	Tokens,
	NAV4_HORIZONTAL_NAV_HEIGHT,
} from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useSetMostRecentlyViewedBoard } from '@atlassian/jira-horizontal-nav-common/src/services/set-most-recently-viewed-board/useSetMostRecentlyViewedBoard.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';

const AmplitudeSessionReplay = importCond<
	typeof import('@atlassian/jira-amplitude-session-replay/src/index.tsx'),
	typeof import('@atlassian/jira-amplitude-session-replay/src/empty.tsx')
>(
	'jira_amplitude_sr_phase_2',
	'@atlassian/jira-amplitude-session-replay/src/index.tsx',
	'@atlassian/jira-amplitude-session-replay/src/empty.tsx',
);

export function callWhenIdle(callback: () => void): number | ReturnType<typeof setTimeout> {
	if ('requestIdleCallback' in window && typeof requestIdleCallback === 'function') {
		return requestIdleCallback(callback);
	}

	if ('requestAnimationFrame' in window && typeof requestAnimationFrame === 'function') {
		return requestAnimationFrame(callback);
	}

	return setTimeout(callback, 0);
}

export type MostRecentlyViewedBoard = {
	scopeId: string;
	projectId: string;
};

type Props = {
	children: ReactNode;
	mostRecentlyViewedBoard?: MostRecentlyViewedBoard;
};

const NavWrapper = ({ children, mostRecentlyViewedBoard }: Props): JSX.Element => {
	const isPageHydrating = useIsHydrateRenderMode();
	const [isFullscreen] = useIsFullscreen();
	const { isSoftwareProject } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareProject: false };

	const setMostRecentlyViewedBoard = useSetMostRecentlyViewedBoard();

	useEffect(() => {
		callWhenIdle(() => {
			if (mostRecentlyViewedBoard) {
				setMostRecentlyViewedBoard?.(
					mostRecentlyViewedBoard.scopeId,
					mostRecentlyViewedBoard.projectId,
				);
			}
		});
	}, [setMostRecentlyViewedBoard, mostRecentlyViewedBoard]);

	let ssrPlaceholderProps = {};
	if (!isPageHydrating) {
		ssrPlaceholderProps = __SERVER__
			? { 'data-ssr-placeholder': 'project-view-navigation' }
			: { 'data-ssr-placeholder-replace': 'project-view-navigation' };
	}

	useEffect(() => {
		if (expVal('jira_amplitude_sr_phase_2_exp', 'isEnabled', false)) {
			callWhenIdle(() => {
				AmplitudeSessionReplay.init();
			});
			return () => {
				AmplitudeSessionReplay.stop();
			};
		}
	}, []);

	return (
		<UFOSegment name="jira-horizontal-nav">
			<JSErrorBoundary
				id="HorizontalNavErrorBoundary"
				packageName="jiraHorizontalNav"
				fallback={HorizontalNavSkeleton}
			>
				<header
					id="ak-project-view-navigation"
					css={[!isSoftwareProject && headerStyles]}
					data-testid="horizontal-nav.ui.content.horizontal-nav"
					{...ssrPlaceholderProps}
				>
					<Grid
						gap="space.050"
						alignItems="end"
						xcss={[
							contentStyles,
							!isFullscreen && minHeightStyle,
							!isSoftwareProject && relativePositionStyle,
						]}
					>
						{children}
					</Grid>
				</header>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

export default NavWrapper;

const minHeightStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	minHeight: `${NAV4_HORIZONTAL_NAV_HEIGHT}px`,
});

const contentStyles = xcss({
	paddingTop: 'space.150',
});

// Delete headerStyles when business projects are migrated to the new theming
const headerStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: Tokens.ELEVATION_SURFACE_CONTRAST,
	boxSizing: 'border-box',
	position: 'relative',
	'&::before': {
		content: ' ',
		position: 'absolute',
		height: '100%',
		width: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backdropFilter: Tokens.BACKDROP_FILTER,
		top: 0,
		left: 0,
	},
});

// Delete relativePositionStyle when business projects are migrated to the new theming
const relativePositionStyle = xcss({
	position: 'relative',
});
